// ============================================================================
// StockMovementCard — propose_stock_movement (copilot-stock-in-one-sentence,
// 2026-10-02; TCSD-0384/0385/0386/0388).
//
// The server resolved everything (am-copilot resolveStockMovement) and sent it
// as input.resolved: the product, the clinic with its owner in parentheses, the
// stock locations serving that clinic with what each holds, the one it
// pre-selected, and before → after. The account manager is never asked where it
// came from: the choice is on the card and the tap is the confirmation.
// The commit goes through the existing stock writes in dialysis-stock/api.ts, as
// the signed-in user (TCSD-0321) — the Co-pilot never writes stock itself.
// ⛔ A source going below zero is warned, never blocked (TCSD-0385).
// ============================================================================

import { useMemo, useState } from 'react';
import { Package } from 'lucide-react';
import type { CopilotProposalBase } from '../../../shared/types';
import { CardShell } from './card-shell';

export interface StockChoice {
  office_id: string;
  office_name: string;
  on_hand: number;
  is_default: boolean;
}

/** The shape am-copilot puts in input.resolved. */
export interface StockResolved {
  kind: 'delivery' | 'transfer' | 'count' | 'pickup';
  qty: number;
  unit: string;
  occurred_at: string | null;
  notes: string | null;
  supply_id: string;
  item_name: string;
  // delivery / pickup
  clinic?: { facility_id: string; name: string; before: number | null; after: number | null };
  choices?: StockChoice[];
  selected_office_id?: string;
  show_choices?: boolean;
  // transfer
  from?: { office_id: string; name: string; before: number; after: number };
  to?: { office_id: string; name: string; before: number; after: number };
  // count
  at?: { office_id: string | null; facility_id: string | null; name: string };
  before?: number | null;
  after?: number;
  change?: number | null;
  adds_to_on_hand?: boolean;
  cost_note?: string;
  warning?: string | null;
}

const KIND_TITLE: Record<StockResolved['kind'], string> = {
  delivery: 'Stock delivery',
  transfer: 'Stock transfer',
  count: 'Stock count',
  pickup: 'Stock pickup',
};

function qtyText(n: number | null | undefined, unit: string): string {
  if (n == null) return 'not counted yet';
  const v = unit === 'tabs' ? Math.round(n).toLocaleString('en-US') : (Math.round(n * 10) / 10).toLocaleString('en-US');
  return `${v} ${unit}`;
}

const shortWarning = (n: number, unit: string) =>
  `Records show ${qtyText(n, unit)} here — this will show a negative balance until someone counts.`;

interface StockMovementCardProps {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

export function StockMovementCard({ proposal, isCommitting, onDoIt, onDiscard }: StockMovementCardProps) {
  const r = (proposal.input as { resolved?: StockResolved }).resolved;
  const [selected, setSelected] = useState(r?.selected_office_id ?? '');

  const choice = useMemo(() => r?.choices?.find((c) => c.office_id === selected) ?? null, [r, selected]);

  if (!r) {
    return (
      <CardShell
        title="Stock"
        icon={<Package className="h-4 w-4" />}
        status={proposal.status}
        doItDisabled
        doItDisabledReason="Nothing was worked out for this card — ask again."
        onDoIt={() => undefined}
        onDiscard={onDiscard}
      >
        <p className="text-sm text-tcs-text-secondary">This card could not be filled in.</p>
      </CardShell>
    );
  }

  const unit = r.unit;
  const wasEdited = selected !== (r.selected_office_id ?? '');
  const product = `${r.item_name.startsWith('Stearns') ? 'Stearns tablets' : r.item_name} · ${qtyText(r.qty, unit)}`;

  // Before → after for the location the user has picked (delivery out of it, pickup into it).
  const locBefore = choice?.on_hand ?? 0;
  const locAfter = r.kind === 'delivery' ? locBefore - r.qty : locBefore + r.qty;
  const warning =
    r.kind === 'delivery'
      ? locAfter < 0 ? shortWarning(locBefore, unit) : null
      : r.warning ?? null;

  return (
    <CardShell
      title={KIND_TITLE[r.kind]}
      icon={<Package className="h-4 w-4" />}
      status={proposal.status}
      committedLabel={proposal.target_row_id ? `Saved as ${proposal.target_row_id}` : 'Saved'}
      isCommitting={isCommitting}
      doItDisabled={(r.kind === 'delivery' || r.kind === 'pickup') && !choice}
      doItDisabledReason="Pick the stock location."
      onDoIt={() => onDoIt({ selected_office_id: selected || null, resolved: r }, wasEdited)}
      onDiscard={onDiscard}
    >
      <Row label="Product">{product}</Row>

      {(r.kind === 'delivery' || r.kind === 'pickup') && r.clinic && (
        <>
          <Row label={r.kind === 'delivery' ? 'To' : 'From'}>
            {r.clinic.name}
            <BeforeAfter before={r.clinic.before} after={r.clinic.after} unit={unit} />
          </Row>
          <fieldset>
            <legend className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
              {r.kind === 'delivery' ? 'From' : 'Back to'}
            </legend>
            {r.show_choices ? (
              <div className="space-y-1.5">
                {(r.choices ?? []).map((c) => (
                  <label key={c.office_id} className="flex items-center gap-2 text-sm text-tcs-text">
                    <input
                      type="radio"
                      name={`stock-src-${proposal.action_id}`}
                      checked={selected === c.office_id}
                      onChange={() => setSelected(c.office_id)}
                    />
                    <span className="font-medium">{c.office_name}</span>
                    <span className="text-xs text-tcs-text-muted">{qtyText(c.on_hand, unit)} on hand</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-tcs-text">
                {choice?.office_name ?? '—'}{' '}
                <span className="text-xs text-tcs-text-muted">{qtyText(choice?.on_hand ?? 0, unit)} on hand</span>
              </p>
            )}
            {choice && <BeforeAfter before={locBefore} after={locAfter} unit={unit} />}
          </fieldset>
        </>
      )}

      {r.kind === 'transfer' && r.from && r.to && (
        <>
          <Row label="From">
            {r.from.name}
            <BeforeAfter before={r.from.before} after={r.from.after} unit={unit} />
          </Row>
          <Row label="To">
            {r.to.name}
            <BeforeAfter before={r.to.before} after={r.to.after} unit={unit} />
          </Row>
        </>
      )}

      {r.kind === 'count' && r.at && (
        <>
          <Row label="At">
            {r.at.name}
            <BeforeAfter before={r.before ?? null} after={r.after ?? null} unit={unit} />
          </Row>
          {r.change != null && (
            <p className="text-xs text-tcs-text-secondary">
              Change: {r.change > 0 ? '+' : ''}
              {qtyText(r.change, unit)}
              {r.adds_to_on_hand ? ' — added to what was already there' : ''}
            </p>
          )}
          {r.cost_note && <p className="text-xs text-tcs-text-secondary">{r.cost_note}</p>}
        </>
      )}

      {r.notes && <Row label="Note">{r.notes}</Row>}

      {warning && (
        <p role="alert" className="rounded border border-st-warn-border bg-st-warn-bg px-2 py-1.5 text-xs text-st-warn-fg-strong">
          {warning}
        </p>
      )}
    </CardShell>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="mb-0.5 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      <div className="text-sm text-tcs-text">{children}</div>
    </div>
  );
}

function BeforeAfter({ before, after, unit }: { before: number | null; after: number | null; unit: string }) {
  if (before == null && after == null) {
    return <span className="mt-0.5 block text-xs text-tcs-text-muted">Shelf not counted yet</span>;
  }
  return (
    <span className="mt-0.5 block text-xs text-tcs-text-muted">
      On hand {qtyText(before, unit)} → {qtyText(after, unit)}
    </span>
  );
}
